import { useTranslation } from 'react-i18next';
import { Recipient } from '../../../../models/parcelDeliveryMiles';
import {
  TextLabel,
  FontSizes,
  TextBody,
} from '../../../../common/typography/typography.styles';
import { copyToClipboard } from '../../../../utils/generic/generic.utils';
import {
  DeliveryDetailsContainer,
  DeliveryDetailsHeader,
  PostName,
  PostImage,
  TrackingNumber,
  Value,
  Row,
  DeliveryDetailsFlexContainer,
  SubjectAddressValue,
} from '../details-sidebar.styles';
import { getSubjectDetailsSummary } from '../../../../utils/parcel-creation/parcel-creation-utils';
import { ReactComponent as CopyIcon } from '../../../../assets/copy-icon.svg';

interface Props {
  subject: Recipient;
  trackingNumber?: string;
  title?: string;
}

const DeliveryDetail = ({ subject, trackingNumber, title }: Props) => {
  const { t } = useTranslation();

  const successMessage = t('parcel_number_is_copied');

  const {
    carrier: {
      logo,
      custom_logo: customLogo,
      pudo_info: pudoInfo,
      branch_uuid: branchUUID,
    } = {},
    address: {
      city,
      building,
      street,
      apartment,
      section,
      note,
      buzz_code: buzzCode,
    } = {},
  } = subject;

  const carrier = getSubjectDetailsSummary({
    building,
    street,
    apartment,
    section,
    buzzCode,
    city,
  });
  const fullAddress = pudoInfo ? pudoInfo.full_address : null;
  const pudoWorkingTime = pudoInfo?.working_time_partly_separated?.[0]
    ?.operating_hours?.open_time
    ? `${pudoInfo.working_time_partly_separated[0].operating_hours.open_time} - ${pudoInfo.working_time_partly_separated[0].operating_hours.close_time}`
    : null;

  const hasAddressInfo = !!carrier || !!fullAddress;

  const subjectAddress = [
    subject.country_name,
    subject.address.post_code,
    subject.address.city,
  ].join(', ');

  return (
    <Row $isDelivery={!title}>
      {title && <TextBody>{title}</TextBody>}
      <DeliveryDetailsContainer>
        <DeliveryDetailsHeader $gap='8px'>
          <SubjectAddressValue>{subjectAddress}</SubjectAddressValue>
        </DeliveryDetailsHeader>
        <DeliveryDetailsFlexContainer
          $gap='12px'
          $align='center'
          $mb='0.4rem'>
          <PostName>
            {branchUUID
              ? subject.carrier.branch_name || carrier
              : t('address_delivery')}
          </PostName>
          {(customLogo || logo) && (
            <PostImage
              src={customLogo ?? logo}
              alt=''
            />
          )}
        </DeliveryDetailsFlexContainer>
        {!hasAddressInfo && <Value>{t('no_info')}</Value>}
        {hasAddressInfo && (
          <TextBody
            $color='#2c363f'
            $wordBreak='break-all'>
            {branchUUID ? fullAddress : carrier}
          </TextBody>
        )}
        {pudoWorkingTime && hasAddressInfo && (
          <TextLabel
            $mt='4px'
            as='p'
            $fontSize={FontSizes.small}>
            {pudoWorkingTime}
          </TextLabel>
        )}
        {note && (
          <TextBody
            $mt='2px'
            $color='#515151'
            $fontSize={FontSizes.small}
            $wordBreak='break-all'>
            {note}
          </TextBody>
        )}
        {trackingNumber && (
          <TrackingNumber
            type='button'
            $mt='1.2rem'
            onClick={() =>
              copyToClipboard({
                text: trackingNumber,
                successMessage,
              })
            }>
            <TextBody
              as='span'
              $color='currentColor'>
              {trackingNumber}
            </TextBody>
            <CopyIcon />
          </TrackingNumber>
        )}
      </DeliveryDetailsContainer>
    </Row>
  );
};

export default DeliveryDetail;
